import { faker } from "@faker-js/faker";
import { searchTypes } from "../../modules/aml/aml.constant";
import {
  Address,
  AdverseMedia,
  Alias,
  AmlBusinessSearchHits,
  AmlDetails,
  AmlIndividualSearchHit,
  AMLResultDetails,
  AmlSearchResult,
  BulkScreeningStatus,
  BusinessLink,
  DisqualifiedDirector,
  DisqualifiedDirectorValue,
  Enforcement,
  Event,
  IndividualLink,
  InsolvencyRegister,
  InsolvencyValue,
  Pep,
  PepValue,
  Position,
  ProfileOfInterest,
  Sanctions,
  SanctionValue,
  Source,
  StateOwnedEnterprise,
  TypeAndValue,
} from "../../modules/aml/types/aml.type";
import { AmlAlerts, KycAlerts } from "../../modules/profiles/types/alerts.type";
import { AmlSearchesType } from "../../modules/profiles/types/amlScreen.type";
import {
  amlScreenMatchDecisions,
  AmlTabsTypes,
  Gender,
  PepTiers,
  ProfileStatus,
  RiskRating,
  SearchType,
  Status,
} from "../../shared/constants/common.constant";
import { Tab as TabType } from "../../shared/types/tab.type";
import {
  KeyPartiesType,
  ProfileTypes,
} from "../../modules/profiles/types/profile.type";
import { randomSingleDigitNumber } from "./utils";
import {
  HitDecisions,
  ReportsListItem,
  ReportsStatus,
} from "../../modules/generate-reports/report.types";

const aliasType = [
  "Name Spelling Variation",
  "DBA",
  "Trade Name",
  "Acronym",
  "Original Script Name",
];

export const countriesType = [
  "fr",
  "nl",
  "in",
  "af",
  "ax",
  "al",
  "ua",
  "ae",
  "gb",
  "un",
];

export const AmlDetailsBuilder = (): AmlDetails => {
  const datasetCount = faker.datatype.number({ min: 1, max: 5 });
  const imageUrlCount = faker.datatype.number({ min: 1, max: 5 });
  const activitiesCount = faker.datatype.number({ min: 1, max: 5 });
  const aliasesCount = faker.datatype.number({ min: 1, max: 5 });
  const identifiersCount = faker.datatype.number({ min: 1, max: 5 });
  const notesCount = faker.datatype.number({ min: 1, max: 5 });
  const individualLinksCount = faker.datatype.number({ min: 1, max: 10 });
  const businessLinksCount = faker.datatype.number({ min: 1, max: 10 });
  const dateCount = faker.datatype.number({ min: 1, max: 5 });

  return {
    id: faker.random.word(),
    hitScore: faker.datatype.number(),
    name: faker.company.name(),
    match: faker.random.word(),
    note: faker.random.word(),
    countries: Array.from({ length: activitiesCount }, () =>
      faker.random.word()
    ),
    datasets: Array.from({ length: datasetCount }, () =>
      faker.helpers.arrayElement(["POI", "SAN", "ENF", "INS", "DD"])
    ),
    version: faker.random.word(),
    modifiedAt: faker.random.word(),
    modifiedById: faker.datatype.number(),
    modifiedBy: faker.random.word(),
    createdAt: faker.random.word(),
    decision: faker.random.word(),
    firstName: faker.name.firstName(),
    middleName: faker.name.middleName(),
    lastName: faker.name.lastName(),
    gender: faker.helpers.arrayElement(["Male", "Female", "Others"]),
    datesOfBirth: Array.from({ length: dateCount }, () =>
      faker.date.birthdate().toISOString()
    ),
    isDeceased: faker.datatype.boolean(),
    datesOfDeath: Array.from({ length: dateCount }, () =>
      faker.date.birthdate().toISOString()
    ),
    businessTypes: Array.from({ length: imageUrlCount }, () =>
      faker.random.word()
    ),
    description: faker.random.words(20),
    activities: Array.from({ length: activitiesCount }, () =>
      faker.random.word()
    ),
    pepTier: "tier1",
    profileImages: Array.from({ length: imageUrlCount }, () =>
      faker.image.imageUrl()
    ),
    screeningNotes: Array.from({ length: notesCount }, () =>
      faker.random.alphaNumeric(25)
    ),
    aliases: Array.from({ length: aliasesCount }, () => generateAlias()),
    contacts: generateContacts(),
    individualLinks: Array.from({ length: individualLinksCount }, () =>
      generateIndividualLinks()
    ),
    businessLinks: Array.from({ length: businessLinksCount }, () =>
      generateBusinessLinks()
    ),
    addresses: Array.from({ length: activitiesCount }, () => generateAddress()),
    identifiers: Array.from({ length: identifiersCount }, () => {
      return {
        type: faker.random.word(),
        value: faker.random.alphaNumeric(20),
      };
    }),
    sources: Array.from({ length: activitiesCount }, () => generateSources()),
    amlResults: AmlResultDetailsBuilder(),
  };
};

export const amlTabsBuilder = (): TabType[] => [
  {
    id: AmlTabsTypes.about,
    label: "aml.tabs.about",
    name: "about",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.contactAndAddresses,
    label: "aml.tabs.contactAndAddresses",
    name: "contactAndAddresses",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.amlCategories,
    label: "aml.tabs.amlCategories",
    name: "amlCategories",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.businessLinks,
    label: "aml.tabs.businessLinks",
    name: "businessLinks",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.individualLinks,
    label: "aml.tabs.individualLinks",
    name: "individualLinks",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.sources,
    label: "aml.tabs.sources",
    name: "sources",
    noOfItems: 0,
  },
];

export const AmlSearchResults = (noOfRecords = 1): AmlSearchResult[] => {
  const imageUrlCount = faker.datatype.number({ min: 0, max: 5 });
  return Array.from({ length: noOfRecords }, () => {
    const randomNoOfCountries = faker.datatype.number({ min: 1, max: 3 });
    const randomNoOfCategories = faker.datatype.number({ min: 1, max: 3 });

    return {
      name: faker.company.name(),
      countries: Array.from({ length: randomNoOfCountries }, () =>
        faker.helpers.arrayElement([
          "fr",
          "nl",
          "in",
          "af",
          "ax",
          "al",
          "ua",
          "ae",
          "gb",
          "un",
        ])
      ),
      datasets: Array.from({ length: randomNoOfCategories }, () =>
        faker.helpers.arrayElement(["POI", "SAN", "ENF", "INS", "DD"])
      ),
      match: faker.helpers.arrayElement(["90", "95", "100"]),
      gender: faker.helpers.arrayElement(["Male", "Female", "Others"]),
      datesOfBirth: [faker.date.past().toISOString()],
      id: faker.datatype.uuid(),
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
      modifiedBy: "raj",
      modifiedById: faker.helpers.arrayElement([
        101550907, 101551112, 101551104, 101551110, 101551115,
      ]),
      pepTier: faker.helpers.arrayElement(["PEP Tier 1", "PEP Tier 3"]),
      profileImages: Array.from({ length: imageUrlCount }, () =>
        faker.image.imageUrl()
      ),
      note: faker.company.name(),
      hitScore: faker.helpers.arrayElement([90, 95, 100]),
      decision: faker.company.name(),
      firstName: faker.company.name(),
      middleName: faker.company.name(),
      lastName: faker.company.name(),
    };
  });
};

export const AmlBusinessResults = (noOfRecords = 1): AmlSearchesType[] => {
  return Array.from({ length: noOfRecords }, () => {
    const randomNoOfDataset = faker.datatype.number({ min: 0, max: 3 });
    return {
      id: faker.datatype.uuid(),
      name: faker.random.word(),
      countryCodes: faker.helpers.arrayElements(countriesType),
      threshold: faker.datatype.number(),
      type: SearchType.Business,
      status: faker.helpers.arrayElement([
        Status.New,
        Status.Approved,
        Status.Declined,
        Status.Pending,
        Status.Cancelled,
        Status.Referred,
        Status.Closed,
      ]),
      datasets: Array.from({ length: randomNoOfDataset }, () =>
        faker.helpers.arrayElement(["PEP", "AM", "SAN"])
      ),
      riskRating: faker.helpers.arrayElement([
        RiskRating.VeryHigh,
        RiskRating.High,
        RiskRating.Medium,
        RiskRating.Low,
        RiskRating.VeryLow,
        RiskRating.NotApplicable,
      ]),
      assignedToUserId: faker.helpers.arrayElement([
        101550907, 101551112, 101551104, 101551110, 101551115,
      ]),
      assignedUser: faker.helpers.arrayElement([faker.internet.userName(), ""]),
      createdById: faker.datatype.number(),
      createdBy: faker.random.word(),
      createdAt: faker.date.past(1).toISOString(),
      modifiedById: 0,
      modifiedBy: faker.internet.userName(),
      modifiedAt: faker.date.recent().toISOString(),
      note: null,
      totalHitCount: faker.datatype.number(),
      truePositiveHitsCount: faker.datatype.number(),
      falsePositiveHitsCount: faker.datatype.number(),
      undecidedHitsCount: faker.datatype.number(),
      firstName: null,
      middleName: null,
      lastName: null,
      gender: null,
      dateOfBirth: null,
      pepTiers: faker.helpers.arrayElements([
        PepTiers.PepTier1,
        PepTiers.PepTier2,
        PepTiers.PepTier3,
      ]),
      scheduleId: faker.datatype.uuid(),
      keyPartyId: faker.datatype.uuid(),
    };
  });
};

export const AmlKeyPartyIndividualResults = (noOfRecords = 1) => {
  return Array.from({ length: noOfRecords }, () => {
    const randomNoOfDataset = faker.datatype.number({ min: 0, max: 3 });
    const randomNoOfCountries = faker.datatype.number({ min: 1, max: 3 });
    return {
      id: faker.datatype.uuid(),
      name: faker.random.word(),
      keypartyId: faker.datatype.uuid(),
      keypartyType: searchTypes.individual,
      searchId: faker.datatype.uuid(),
      createdByUserId: faker.datatype.number(),
      createdBy: faker.random.word(),
      createdAt: new Date().toISOString(),
      monitored: faker.datatype.boolean(),
      datasets: Array.from({ length: randomNoOfDataset }, () =>
        faker.helpers.arrayElement(["PEP", "AM", "SAN"])
      ),
      scheduleId: faker.helpers.arrayElement([faker.datatype.uuid(), null]),
      gender: faker.helpers.arrayElement([
        Gender.male,
        Gender.female,
        undefined,
      ]),
      risk: faker.helpers.arrayElement([
        RiskRating.VeryHigh,
        RiskRating.High,
        RiskRating.Medium,
        RiskRating.Low,
        RiskRating.VeryLow,
        RiskRating.NotApplicable,
      ]),
      riskRating: faker.helpers.arrayElement([
        RiskRating.VeryHigh,
        RiskRating.High,
        RiskRating.Medium,
        RiskRating.Low,
        RiskRating.VeryLow,
        RiskRating.NotApplicable,
      ]),
      status: faker.helpers.arrayElement([
        ProfileStatus.new,
        ProfileStatus.approved,
        ProfileStatus.declined,
        ProfileStatus.pending,
        ProfileStatus.cancelled,
        ProfileStatus.referred,
        ProfileStatus.closed,
      ]),
      assignedTo: faker.helpers.arrayElement([faker.internet.userName(), ""]),
      assignedToUser: faker.helpers.arrayElement([
        faker.internet.userName(),
        "",
      ]),
      country: faker.helpers.arrayElement(countriesType),
      assignedToUserId: faker.helpers.arrayElement([
        101550907, 101551112, 101551104, 101551110, 101551115,
      ]),
      countries: Array.from({ length: randomNoOfCountries }, () =>
        faker.helpers.arrayElement([
          "fr",
          "nl",
          "in",
          "af",
          "ax",
          "al",
          "ua",
          "ae",
          "gb",
          "un",
        ])
      ),
      countryCodes: Array.from({ length: randomNoOfCountries }, () =>
        faker.helpers.arrayElement([
          "fr",
          "nl",
          "in",
          "af",
          "ax",
          "al",
          "ua",
          "ae",
          "gb",
          "un",
        ])
      ),

      type: searchTypes.individual,
      threshold: faker.helpers.arrayElement([90, 95, 100]),
      dateOfBirth: faker.date.past().toISOString(),
      matching: faker.helpers.arrayElement(["90", "95", "100"]),
      dobMatching: faker.helpers.arrayElement(["90", "95", "100"]),
      pepTiers: Array.from({ length: randomNoOfCountries }, () =>
        faker.helpers.arrayElement(["PEP Tier 1", "PEP Tier 3"])
      ),
      note: faker.company.name(),
      modifiedByUserId: faker.datatype.number(),
      modifiedBy: faker.random.word(),
      modifiedAt: new Date().toISOString(),
      deletedBy: faker.random.word(),
      deletedByUserId: faker.datatype.number(),
      deletedAt: new Date().toISOString(),
      isDeleted: false,
      totalHitCount: 5,
      truePositiveHitsCount: 3,
      undecidedHitsCount: 0,
      falsePositiveHitsCount: 2,
    };
  });
};

export const AmlIndividualSearchHitResults = (
  noOfRecords = 11
): AmlIndividualSearchHit[] => {
  return Array.from({ length: noOfRecords }, () => {
    const randomNoOfDataset = faker.datatype.number({ min: 0, max: 3 });
    const randomNoOfCountries = faker.datatype.number({ min: 1, max: 3 });
    const name = faker.random.word();
    return {
      id: faker.datatype.uuid(),
      hitScore: faker.datatype.number({ min: 75, max: 85 }),
      name,
      match: faker.random.word(),
      countries: Array.from({ length: randomNoOfCountries }, () =>
        faker.helpers.arrayElement([
          "fr",
          "nl",
          "in",
          "af",
          "ax",
          "al",
          "ua",
          "ae",
          "gb",
          "un",
        ])
      ),
      datasets: Array.from({ length: randomNoOfDataset }, () =>
        faker.helpers.arrayElement(["PEP", "AM", "SAN"])
      ),
      decision: amlScreenMatchDecisions.undecided,
      note: faker.helpers.arrayElement(["Sample Note", "", null]),
      modifiedById: 985658,
      modifiedBy: faker.random.word(),
      modifiedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      firstName: faker.random.word(),
      middleName: faker.random.word(),
      lastName: faker.random.word(),
      gender: "Male",
      datesOfBirth: [faker.date.birthdate().toISOString()],
      pepTier: "PEP Tier 1",
      profileImages: [faker.image.imageUrl()],
    };
  });
};

export const AmlBusinessSearchHitResults = (
  noOfRecords = 1
): AmlBusinessSearchHits[] => {
  return Array.from({ length: noOfRecords }, () => {
    const randomNoOfDataset = faker.datatype.number({ min: 0, max: 3 });
    const randomNoOfCountries = faker.datatype.number({ min: 1, max: 3 });
    const name = faker.random.word();

    return {
      id: faker.datatype.uuid(),
      hitScore: faker.datatype.number({ min: 75, max: 85 }),
      name,
      match: faker.random.word(),
      countries: Array.from({ length: randomNoOfCountries }, () =>
        faker.helpers.arrayElement([
          "fr",
          "nl",
          "in",
          "af",
          "ax",
          "al",
          "ua",
          "ae",
          "gb",
          "un",
        ])
      ),
      datasets: Array.from({ length: randomNoOfDataset }, () =>
        faker.helpers.arrayElement(["PEP", "AM", "SAN"])
      ),
      decision: amlScreenMatchDecisions.undecided,
      note: faker.helpers.arrayElement(["Sample Note", "", null]),
      modifiedById: 985658,
      modifiedBy: faker.random.word(),
      modifiedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      firstName: faker.random.word(),
      middleName: faker.random.word(),
      lastName: faker.random.word(),
      gender: "Male",
      datesOfBirth: [faker.date.birthdate().toISOString()],
      pepTier: "PEP Tier 1",
      profileImages: [faker.image.imageUrl()],
    };
  });
};

export const AmlIndividualsResults = (noOfRecords = 1) => {
  return Array.from({ length: noOfRecords }, () => {
    const randomNoOfDataset = faker.datatype.number({ min: 0, max: 3 });
    const randomNoOfCountries = faker.datatype.number({ min: 1, max: 3 });
    return {
      firstName: faker.name.firstName(),
      middleName: faker.name.middleName(),
      lastName: faker.name.lastName(),
      name: faker.random.word(),
      gender: faker.helpers.arrayElement(["Male", "Female"]),
      dateOfBirth: new Date().toISOString(),
      id: faker.datatype.uuid(),
      threshold: faker.datatype.number(),
      type: SearchType.Individual,
      createdByUserId: faker.datatype.number(),
      createdBy: faker.random.word(),
      createdById: faker.datatype.number(),
      createdAt: new Date().toISOString(),
      modifiedAt: faker.random.word(),
      modifiedBy: faker.random.word(),
      monitored: faker.datatype.boolean(),
      scheduleId: faker.helpers.arrayElement([faker.datatype.uuid(), null]),
      note: faker.random.word(),
      countryCodes: faker.helpers.arrayElements(countriesType),
      pepTiers: faker.helpers.arrayElements([
        PepTiers.PepTier1,
        PepTiers.PepTier2,
        PepTiers.PepTier3,
      ]),
      datasets: Array.from({ length: randomNoOfDataset }, () =>
        faker.helpers.arrayElement(["PEP", "AM", "SAN"])
      ),
      riskRating: faker.helpers.arrayElement([
        RiskRating.VeryHigh,
        RiskRating.High,
        RiskRating.Medium,
        RiskRating.Low,
        RiskRating.VeryLow,
        RiskRating.NotApplicable,
      ]),
      risk: faker.helpers.arrayElement([
        RiskRating.VeryHigh,
        RiskRating.High,
        RiskRating.Medium,
        RiskRating.Low,
        RiskRating.VeryLow,
        RiskRating.NotApplicable,
      ]),
      status: faker.helpers.arrayElement([
        Status.New,
        Status.Approved,
        Status.Declined,
        Status.Pending,
        Status.Cancelled,
        Status.Referred,
        Status.Closed,
      ]),
      assignedTo: faker.helpers.arrayElement([faker.internet.userName(), ""]),
      country: faker.helpers.arrayElement(countriesType),
      assignedToUserId: faker.helpers.arrayElement([
        101550907, 101551112, 101551104, 101551110, 101551115,
      ]),
      assignedUser: faker.helpers.arrayElement([faker.internet.userName(), ""]),
      countries: Array.from({ length: randomNoOfCountries }, () =>
        faker.helpers.arrayElement([
          "fr",
          "nl",
          "in",
          "af",
          "ax",
          "al",
          "ua",
          "ae",
          "gb",
          "un",
        ])
      ),
      totalHitCount: faker.datatype.number(),
      truePositiveHitsCount: faker.datatype.number(),
      falsePositiveHitsCount: faker.datatype.number(),
      undecidedHitsCount: faker.datatype.number(),
    };
  });
};

export const amlCategoriesBusinessSubTabs = (): TabType[] => [
  {
    id: AmlTabsTypes.sanctions,
    label: "aml.subTabs.sanctions",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.enforcement,
    label: "aml.subTabs.enforcement",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.insolvencyRegister,
    label: "aml.subTabs.insolvencyRegister",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.stateOwnedEnterprise,
    label: "aml.subTabs.stateOwnedEnterprise",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.profileOfInterest,
    label: "aml.subTabs.profileOfInterest",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.adverseMedia,
    label: "aml.subTabs.adverseMedia",
    noOfItems: 0,
  },
];

export const amlCategoriesIndividualSubTabs = (): TabType[] => [
  {
    id: AmlTabsTypes.sanctions,
    label: "aml.subTabs.sanctions",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.enforcement,
    label: "aml.subTabs.enforcement",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.insolvencyRegister,
    label: "aml.subTabs.insolvencyRegister",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.disqualifiedDirector,
    label: "aml.subTabs.disqualifiedDirector",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.pep,
    label: "aml.subTabs.pep",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.profileOfInterest,
    label: "aml.subTabs.profileOfInterest",
    noOfItems: 0,
  },
  {
    id: AmlTabsTypes.adverseMedia,
    label: "aml.subTabs.adverseMedia",
    noOfItems: 0,
  },
];

export const generateAddress = (): Address => {
  return {
    type: faker.random.word(),
    postalCode: faker.random.word(),
    line1: faker.random.word(),
    line2: faker.random.word(),
    city: faker.random.word(),
    county: faker.random.word(),
    countryCode: faker.random.word(),
  };
};

export const generateContacts = (): TypeAndValue[] => {
  return [
    { type: "telephone", value: faker.phone.number() },
    { type: "fax", value: faker.phone.number() },
    { type: "website", value: faker.internet.url() },
    { type: "email", value: faker.internet.email() },
  ];
};

const generateSources = (): Source => {
  return {
    id: faker.datatype.uuid(),
    title: faker.random.word(),
    description: faker.random.words(10),
    link: faker.internet.url(),
    dateAdded: faker.date.past().toISOString(),
    isCopyrighted: Math.random() < 0.5,
    datePublished: faker.date.past().toISOString(),
    documentLink: faker.internet.url(),
    datasets: Array.from(
      { length: faker.datatype.number({ min: 1, max: 3 }) },
      () => faker.helpers.arrayElement(["PEP", "Enf", "SAN", "Adverse Media"])
    ),
    language: faker.random.word(),
    credibility: faker.random.word(),
  };
};

const generateAlias = (): Alias => ({
  type: faker.helpers.arrayElement(aliasType),
  name: faker.company.name(),
  firstName: faker.name.firstName(),
  middleName: faker.name.middleName(),
  lastName: faker.name.lastName(),
});

const generateEvent = (): Event => ({
  id: faker.random.word(),
  type: faker.random.word(),
  date: faker.date.past().toISOString(),
  amount: parseInt(faker.finance.amount()),
  currencyCode: faker.finance.currencyCode(),
  duration: faker.random.word(),
  sources: Array.from(
    { length: faker.datatype.number({ min: 1, max: 3 }) },
    () => generateSource()
  ),
});

const generateSource = (): Source => ({
  id: faker.datatype.uuid(),
  title: faker.random.word(),
  description: faker.random.word(),
  link: faker.internet.url(),
  dateAdded: faker.date.past().toISOString(),
  isCopyrighted: faker.datatype.boolean(),
  datePublished: faker.date.past().toISOString(),
  documentLink: faker.internet.url(),
  datasets: Array.from(
    { length: faker.datatype.number({ min: 1, max: 3 }) },
    () => faker.helpers.arrayElement(["PEP", "Enf", "SAN", "AM"])
  ),
  credibility: faker.random.word(),
  language: faker.random.word(),
});

const generateSanctionValue = (): SanctionValue => ({
  sanctionId: faker.random.word(),
  issuingBody: faker.random.words(5),
  origin: faker.address.country(),
  sanctionTypes: Array.from(
    { length: faker.datatype.number({ min: 1, max: 3 }) },
    () => faker.random.word()
  ),
  measures: Array.from(
    { length: faker.datatype.number({ min: 1, max: 3 }) },
    () => faker.random.word()
  ),
  regime: faker.name.firstName(),
});

const generateSanctions = (): Sanctions => ({
  events: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateEvent()
  ),
  sources: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateSource()
  ),
  type: faker.random.word(),
  value: generateSanctionValue(),
});

const generateEnforcement = (): Enforcement => ({
  events: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateEvent()
  ),
  sources: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateSource()
  ),
  type: faker.random.word(),
  value: {
    category: faker.random.word(),
    subCategory: faker.random.word(),
  },
});

const insolvencyRegisterValue = (): InsolvencyValue => ({
  insolvencyIdNumber: faker.random.alphaNumeric(12),
  type: faker.random.word(),
  debtAmount: faker.finance.amount(),
  startDate: faker.date.past().toISOString(),
  endDate: faker.date.past().toISOString(),
  caseReferenceNumbers: Array.from(
    { length: faker.datatype.number({ min: 1, max: 3 }) },
    () => faker.random.alphaNumeric(10)
  ),
  solicitor: faker.random.word(),
  court: {
    name: faker.name.firstName(),
    petitioner: faker.name.firstName(),
    hearingDate: faker.date.past().toISOString(),
    petitionPresentedDate: faker.date.past().toISOString(),
  },
  orderDate: faker.date.past().toISOString(),
  gazetteIssuedDate: faker.date.past().toISOString(),
  awardDate: faker.date.past().toISOString(),
});

const generateInsolvencyRegister = (): InsolvencyRegister => ({
  events: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateEvent()
  ),
  sources: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateSource()
  ),
  type: faker.random.word(),
  value: insolvencyRegisterValue(),
});

const generateStateOwnedEnterprise = (): StateOwnedEnterprise => ({
  events: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateEvent()
  ),
  sources: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateSource()
  ),
  type: faker.random.word(),
  value: {
    type: faker.random.word(),
    level: faker.random.word(),
  },
});

const generateDisqualifiedDirectorValue = (): DisqualifiedDirectorValue => ({
  referenceNumber: faker.random.word(),
  fromDate: faker.date.past().toISOString(),
  toDate: faker.date.past().toISOString(),
  organisations: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => faker.company.name()
  ),
  reason: faker.random.words(),
});

const generateDisqualifiedDirector = (): DisqualifiedDirector => ({
  events: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateEvent()
  ),
  sources: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateSource()
  ),
  type: faker.random.word(),
  value: generateDisqualifiedDirectorValue(),
});

const generatePepValue = (): PepValue => ({
  country: faker.address.country(),
  position: faker.name.jobTitle(),
  fromDate: faker.date.past().toISOString(),
  toDate: faker.date.past().toISOString(),
  segment: faker.random.word(),
});

const generatePep = (): Pep => ({
  events: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateEvent()
  ),
  sources: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateSource()
  ),
  type: faker.random.word(),
  value: generatePepValue(),
});

const generatePosition = (): Position => ({
  name: faker.name.jobTitle(),
  segment: faker.random.word(),
  country: faker.address.country(),
  fromDate: faker.date.past().toISOString(),
  toDate: faker.date.past().toISOString(),
});

const generateProfileOfInterest = (): ProfileOfInterest => ({
  events: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateEvent()
  ),
  sources: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateSource()
  ),
  type: faker.random.word(),
  value: {
    category: faker.random.word(),
    positions: Array.from(
      { length: faker.datatype.number({ min: 1, max: 2 }) },
      () => generatePosition()
    ),
  },
});

const generateAdverseMedia = (): AdverseMedia => ({
  events: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateEvent()
  ),
  sources: Array.from(
    { length: faker.datatype.number({ min: 1, max: 2 }) },
    () => generateSource()
  ),
  type: faker.random.word(),
  value: {
    category: faker.random.word(),
    subCategory: faker.random.word(),
  },
});

export const AmlResultDetailsBuilder = (): AMLResultDetails => {
  return {
    SAN: {
      CURRENT: Array.from(
        { length: faker.datatype.number({ min: 1, max: 2 }) },
        () => generateSanctions()
      ),
      FORMER: Array.from(
        { length: faker.datatype.number({ min: 1, max: 2 }) },
        () => generateSanctions()
      ),
    },
    ENF: Array.from({ length: faker.datatype.number({ min: 1, max: 2 }) }, () =>
      generateEnforcement()
    ),
    INS: Array.from({ length: faker.datatype.number({ min: 1, max: 2 }) }, () =>
      generateInsolvencyRegister()
    ),
    SOE: generateStateOwnedEnterprise(),
    POI: Array.from({ length: faker.datatype.number({ min: 1, max: 2 }) }, () =>
      generateProfileOfInterest()
    ),
    AM: Array.from({ length: faker.datatype.number({ min: 1, max: 2 }) }, () =>
      generateAdverseMedia()
    ),
    DD: Array.from({ length: faker.datatype.number({ min: 1, max: 2 }) }, () =>
      generateDisqualifiedDirector()
    ),
    PEP: {
      CURRENT: Array.from(
        { length: faker.datatype.number({ min: 1, max: 2 }) },
        () => generatePep()
      ),
      FORMER: Array.from(
        { length: faker.datatype.number({ min: 1, max: 2 }) },
        () => generatePep()
      ),
    },
    PEPLINKED: {
      events: Array.from(
        { length: faker.datatype.number({ min: 1, max: 2 }) },
        () => generateEvent()
      ),
    },
  };
};

const generateIndividualLinks = (): IndividualLink => {
  const datasetCount = faker.datatype.number({ min: 1, max: 3 });
  return {
    firstName: faker.name.firstName(),
    middleName: faker.name.middleName(),
    lastName: faker.name.lastName(),
    relationship: faker.random.words(6),
    ownershipPercentage: faker.datatype.number({ min: 1, max: 100 }),
    datasets: Array.from({ length: datasetCount }, () =>
      faker.helpers.arrayElement(["POI", "SAN", "ENF", "INS", "DD"])
    ),
  };
};

export const generateBusinessLinks = (): BusinessLink => {
  const datasetCount = faker.datatype.number({ min: 1, max: 3 });
  return {
    name: faker.company.name(),
    relationship: faker.random.words(6),
    ownershipPercentage: faker.datatype.number({ min: 1, max: 100 }),
    datasets: Array.from({ length: datasetCount }, () =>
      faker.helpers.arrayElement(["POI", "SAN", "ENF", "INS", "DD"])
    ),
    individualLinks: Array.from({ length: datasetCount }, () =>
      generateIndividualLinks()
    ),
  };
};

export const generateKeyPartiesDatasets = (datasetCount: number): string[] => {
  const uniqueWords = new Set<string>();

  while (uniqueWords.size < datasetCount) {
    const randomWord = faker.random.word();
    uniqueWords.add(randomWord);
  }

  return Array.from(uniqueWords);
};

export const generateDataForKeyParties = (
  noOfRecords: number
): KeyPartiesType[] => {
  return Array.from({ length: noOfRecords }, () => ({
    id: "123456789",
    name: faker.name.firstName(),
    dateOfBirth: faker.date.past().toISOString(),
    keyPartyType: randomSingleDigitNumber() < 7 ? "shareholder" : "director",
    entityType: ProfileTypes.individual,
    modifiedById: 0,
    modifiedBy: "",
    modifiedAt: "",
    createdAt: "",
    createdById: 0,
    createdBy: "",
  }));
};

export const GenerateAmlAlerts = (
  noOfRecords = 1,
  conditionBasedHitScore = false,
  overrides = {}
): AmlAlerts[] => {
  const randomNoOfCountries = faker.datatype.number({ min: 1, max: 3 });

  return Array.from({ length: noOfRecords }, (_, index) => ({
    id: faker.datatype.uuid(),
    searchId: faker.datatype.uuid(),
    name: faker.name.firstName(),
    date: faker.date.past().toISOString(),
    countries: Array.from({ length: randomNoOfCountries }, () =>
      faker.helpers.arrayElement([
        "MA",
        "US",
        "GB",
        "BE",
        "BE",
        "NL",
        "DE",
        "CA",
        "IE",
        "JP",
        "IT",
        "NO",
        "DK",
        "FR",
        "CA",
      ])
    ),
    datasets: [
      faker.helpers.arrayElement([
        "SAN",
        "SAN-CURRENT",
        "SAN-FORMER",
        "SAN",
        "SAN-CURRENT",
        "ENF",
        "INS",
        "SOE",
        "SOE-CURRENT",
        "SOE-FORMER",
        "SOE-CURRENT",
        "PEP",
        "PEP",
        "PEP",
        "PEP",
        "POI",
        "AM",
      ]),
    ],
    match: "",
    hitScore:
      (index + 1) % 5 === 0 && conditionBasedHitScore
        ? 0
        : faker.helpers.arrayElement([75, 80, 85, 90, 95, 100]),
    decision: "0",
    matchStatus: "0",
    modifiedById: 0,
    modifiedBy: "",
    modifiedAt: "",
    createdAt: "",
    note: faker.helpers.arrayElement([null, "Sample Note"]),
    ...overrides,
  }));
};

export const GenerateKycAlerts = (noOfRecords = 1): KycAlerts[] => {
  return Array.from({ length: noOfRecords }, () => ({
    id: faker.datatype.uuid(),
    date: faker.date.past().toISOString(),
    event: faker.name.jobTitle(),
    description: faker.random.word(),
    safeNumber: faker.datatype.number().toString(),
    previousValue: faker.name.firstName(),
    newValue: faker.name.firstName(),
    note: faker.random.word(),
    createdAt: "2024-04-03T13:34:37.214Z",
    createdById: faker.datatype.number(),
    createdBy: faker.random.word(),
    modifiedAt: "2024-04-03T13:34:37.214Z",
    modifiedById: faker.datatype.number(),
    modifiedBy: faker.random.word(),
    companyId: faker.random.word(),
    ruleName: faker.random.word(),
    eventAt: "2024-04-03T13:34:37.214Z",
    status: "open",
    profileId: "3fa85f64-5717-4562-b3fc-2c963f66afa6",
  }));
};

export const generateBulkScreeningStatus = (
  noOfRecords = 1
): BulkScreeningStatus[] => {
  return Array.from({ length: noOfRecords }, () => ({
    id: faker.datatype.uuid(),
    criteriaCount: faker.datatype.number().toString(),
    jobId: faker.datatype.uuid(),
    startedAt: faker.date.past().toISOString(),
    status: faker.helpers.arrayElement(["submitted", "processing", "failed"]),
    searchesRequested: faker.datatype.number().toString(),
  }));
};

export const generateAMLAlertsReports = (
  noOfRecords = 1
): ReportsListItem[] => {
  return Array.from({ length: noOfRecords }, () => ({
    fileName: faker.random.word(),
    status: faker.helpers.arrayElement([
      ReportsStatus.InProgress,
      ReportsStatus.Failed,
      ReportsStatus.Completed,
      ReportsStatus.Expired,
      ReportsStatus.Queued,
      ReportsStatus.Submitted,
    ]),
    createdByUser: faker.random.word(),
    requestedDate: faker.date.past().toISOString(),
    expiresAt: faker.date.past().toISOString(),
    criteria: {
      fromDate: faker.date.past().toISOString(),
      hitDecisions: [
        faker.helpers.arrayElement([
          HitDecisions.Discarded,
          HitDecisions.FalsePositive,
          HitDecisions.TrueMatch,
          HitDecisions.Undecided,
        ]),
      ],
      toDate: faker.date.past().toISOString(),
      hasHits: faker.datatype.boolean(),
    },
    id: faker.datatype.uuid(),
    type: "aml-alerts",
    createdAt: faker.date.past().toISOString(),
    orderedBy: faker.random.word(),
    expiryDate: faker.date.past().toISOString(),
  }));
};
